import { useState, useEffect, useCallback, useRef } from 'react';
import ConversationList from '../components/ConversationList';
import ChatPanel from '../components/ChatPanel';
import CustomerInfoPanel from '../components/CustomerInfoPanel';
import { useWebSocket } from '../hooks/useWebSocket';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';

const WS_URL = process.env.REACT_APP_WS_URL ||
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`;

// เสียงแจ้งเตือนข้อความเข้า — สร้างจาก Web Audio API (ไม่ต้องมีไฟล์เสียง)
function playNotification() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [[880, 0], [1100, 0.15]].forEach(([freq, delay]) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = ctx.currentTime + delay;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.25, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      osc.start(t);
      osc.stop(t + 0.35);
    });
  } catch (_) {}
}

export default function InboxPage() {
  const { agent, logout } = useAuth();
  const [oaAccounts, setOaAccounts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [filter, setFilter]             = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');
  const [mobileView, setMobileView]     = useState('list');
  const [wsStatus, setWsStatus] = useState('connecting');
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);
  const [pendingImport, setPendingImport] = useState(null);
  const [chatRefreshToken, setChatRefreshToken] = useState(0);
  const [quotaWarn, setQuotaWarn] = useState(null); // { oa_name, used, limit, level }
  const activePanelRef = useRef(null);

  // Initial data load
  useEffect(() => {
    Promise.all([
      api.getOAAccounts(), api.getConversations(), api.getAgents(),
      api.getQuickReplies(), api.getTags(),
    ])
      .then(([oas, convs, ags, qrs, tags]) => {
        setOaAccounts(oas);
        setConversations(convs);
        setAgents(ags);
        setQuickReplies(qrs);
        setAllTags(tags);
      })
      .catch(console.error);
  }, []);

  // WebSocket event handler
  const handleWsMessage = useCallback((event) => {
    setWsStatus('connected');

    if (event.type === 'new_message') {
      const { conversation_id, message, conversation } = event;

      // เสียงแจ้งเตือนเฉพาะข้อความจากลูกค้า (inbound) และ conversation ที่ไม่ใช่ spam
      const convStatus = conversation?.status || conversations.find(c => c.id === conversation_id)?.status;
      if (message.direction === 'inbound' && convStatus !== 'spam') playNotification();

      setConversations(prev => {
        const exists = prev.some(c => c.id === conversation_id);
        if (!exists) {
          // Conversation not in list yet — prepend it (e.g. page was open before this conv was created)
          return [{ ...conversation, unread_count: 1 }, ...prev];
        }
        return prev.map(c =>
          c.id === conversation_id
            ? {
                ...c,
                ...(conversation || {}),          // รับ status ใหม่ (closed → open) จาก server
                last_message: message.content,
                last_message_at: message.created_at,
                unread_count: c.id === activeConv?.id ? 0 : (c.unread_count || 0) + 1,
              }
            : c
        );
      });

      // If this conversation is open in the panel, push the message in
      if (activeConv?.id === conversation_id && activePanelRef.current?._appendMessage) {
        activePanelRef.current._appendMessage(message);
      }
    }

    if (event.type === 'new_conversation') {
      setConversations(prev => {
        if (prev.find(c => c.id === event.conversation.id)) return prev;
        return [event.conversation, ...prev];
      });
    }

    // Image downloaded — update bubble with real image
    if (event.type === 'message_updated') {
      const { conversation_id, message } = event;
      if (activeConv?.id === conversation_id && activePanelRef.current?._updateMessage) {
        activePanelRef.current._updateMessage(message);
      }
    }

    // Private note — add to chat panel only, no unread bump
    if (event.type === 'new_note') {
      const { conversation_id, message } = event;
      if (activeConv?.id === conversation_id && activePanelRef.current?._appendMessage) {
        activePanelRef.current._appendMessage(message);
      }
    }

    if (event.type === 'conversation_updated') {
      setConversations(prev =>
        prev.map(c => (c.id === event.conversation.id ? { ...c, ...event.conversation } : c))
      );
    }

    // Push-quota warning/block from backend — show a dismissible banner
    if (event.type === 'quota_warning') {
      setQuotaWarn(event);
    }
  }, [activeConv?.id]);

  // เมื่อ WS reconnect — trigger ChatPanel ให้โหลดข้อความใหม่เพื่อรับที่หายระหว่าง disconnect
  const handleWsReconnect = useCallback(() => {
    if (activeConv?.id) setChatRefreshToken(t => t + 1);
  }, [activeConv?.id]); // eslint-disable-line

  useWebSocket(WS_URL, {
    onMessage: handleWsMessage,
    onConnect: handleWsReconnect,
    oaIds: oaAccounts.map(o => o.id),
  });

  // Keep ref in sync with activeConv for WS callback
  useEffect(() => {
    activePanelRef.current = activeConv;
  }, [activeConv]);

  function handleSelectConv(conv) {
    setActiveConv(conv);
    setMobileView('chat');
    setConversations(prev =>
      prev.map(c => (c.id === conv.id ? { ...c, unread_count: 0 } : c))
    );
  }

  function handleConversationUpdate(updated) {
    setConversations(prev =>
      prev.map(c => (c.id === updated.id ? { ...c, ...updated } : c))
    );
    if (activeConv?.id === updated.id) setActiveConv(u => ({ ...u, ...updated }));
  }

  async function handleStatusChange(convId, newStatus) {
    try {
      const updated = await api.updateStatus(convId, newStatus);
      handleConversationUpdate(updated);
    } catch (err) { alert(err.message); }
  }

  const wsIndicator = { connected: '#3B6D11', connecting: '#854F0B', disconnected: '#c0392b' };

  function handleLayoutClick(e) {
    // คลิกบริเวณ chat (ไม่ใช่ customer panel) → ปิด info panel
    if (infoPanelOpen && !e.target.closest('.customer-panel')) {
      setInfoPanelOpen(false);
      setPendingImport(null);
    }
  }

  return (
    <div className={`inbox-layout ${mobileView === 'chat' ? 'mobile-chat' : ''}`} onClick={handleLayoutClick}>
      <ConversationList
        conversations={conversations}
        oaAccounts={oaAccounts}
        activeId={activeConv?.id}
        onSelect={handleSelectConv}
        filter={filter}
        onFilterChange={setFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onStatusChange={handleStatusChange}
      />

      <ChatPanel
        conversation={activeConv}
        oaAccounts={oaAccounts}
        agents={agents}
        quickReplies={quickReplies}
        allTags={allTags}
        onConversationUpdate={handleConversationUpdate}
        infoPanelOpen={infoPanelOpen}
        onToggleInfo={() => setInfoPanelOpen(v => !v)}
        refreshToken={chatRefreshToken}
        onMobileBack={() => setMobileView('list')}
        onImportCustomerInfo={(parsed) => {
          setPendingImport({ ...parsed, _ts: Date.now() }); // force re-trigger useEffect
          setInfoPanelOpen(true);
        }}
      />

      {infoPanelOpen && activeConv && (
        <CustomerInfoPanel
          conversation={activeConv}
          pendingImport={pendingImport}
          onClose={() => { setInfoPanelOpen(false); setPendingImport(null); }}
        />
      )}

      {/* Push-quota warning banner */}
      {quotaWarn && (
        <div className={`quota-banner ${quotaWarn.level === 'block' ? 'quota-block' : 'quota-warn'}`}>
          <span className="quota-banner-icon">{quotaWarn.level === 'block' ? '🛑' : '⚠️'}</span>
          <span className="quota-banner-text">
            {quotaWarn.level === 'block'
              ? `${quotaWarn.oa_name}: โควต้า push เต็มแล้ว (${quotaWarn.used?.toLocaleString()}/${quotaWarn.limit?.toLocaleString()}) — ตอบได้เฉพาะ reply ฟรีจนขึ้นเดือนใหม่`
              : `${quotaWarn.oa_name}: โควต้า push ใกล้หมด (${quotaWarn.used?.toLocaleString()}/${quotaWarn.limit?.toLocaleString()}) — เหลือ ~${Math.max(0, quotaWarn.limit - quotaWarn.used).toLocaleString()} ข้อความ`}
          </span>
          <button className="quota-banner-close" onClick={() => setQuotaWarn(null)}>✕</button>
        </div>
      )}

      {/* Floating status bar */}
      <div className="status-bar">
        <span className="ws-dot" style={{ background: wsIndicator[wsStatus] }} />
        <span className="ws-label">{wsStatus === 'connected' ? 'เชื่อมต่อแล้ว' : 'กำลังเชื่อมต่อ...'}</span>
        <span className="agent-name">👤 {agent?.name}</span>
        <button className="btn-logout" onClick={logout}>ออก</button>
      </div>
    </div>
  );
}
